/**
 * Post-login intro dialog: auto-opens once per browser (localStorage latch),
 * re-openable on demand via <WelcomeDialogTrigger>. Frames the demo for a
 * first-time viewer: headline, "what's live" vs "roadmap" panels, and CTAs
 * into the app. Mount <WelcomeDialog> once in the authenticated shell.
 */

import { ArrowRight, CheckCircle2, CircleDot, Info, Sparkles } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

// First-run persistence: the demo overview auto-opens once per browser, then
// stays out of the way. The `vision-deliver-intelligence.` namespace is renamed by the init
// script along with the rest of the placeholder identity.
const WELCOME_SEEN_KEY = "vision-deliver-intelligence.welcome.seen";

// localStorage access can throw (disabled, sandboxed iframe). Both sides are
// guarded so a failure degrades to "already seen" (never nag) rather than
// throwing out of the shell that mounts this.
function readSeen(): boolean {
  if (typeof window === "undefined") {
    return true;
  }
  try {
    return window.localStorage.getItem(WELCOME_SEEN_KEY) === "1";
  } catch {
    return true;
  }
}

function writeSeen(): void {
  try {
    window.localStorage.setItem(WELCOME_SEEN_KEY, "1");
  } catch {
    // Preference just won't persist — not worth surfacing.
  }
}

/** Renders nothing on its own — pops the welcome panel on first visit, then
 *  latches it shut via localStorage. Mount once inside the app shell. */
export function WelcomeDialog() {
  // Lazy initial state reads the flag once during the first render, so the
  // panel is open from the very first paint on a fresh browser — no effect,
  // no set-state-in-effect flicker.
  const [open, setOpen] = useState(() => !readSeen());

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      writeSeen();
    }
    setOpen(next);
  };

  return <WelcomeDialogControlled open={open} onOpenChange={handleOpenChange} />;
}

/** The info button in the header — reopens the overview on demand, regardless
 *  of the first-run latch. */
export function WelcomeDialogTrigger({
  className,
  label = "About this demo",
}: {
  className?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={label}
        className={cn(
          "text-muted hover:bg-accent-light hover:text-foreground inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors",
          className,
        )}
      >
        <Info className="h-5 w-5" />
      </button>
      <WelcomeDialogControlled open={open} onOpenChange={setOpen} />
    </>
  );
}

function WelcomeDialogControlled({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-h-[calc(100vh-2rem)] max-w-2xl gap-0 overflow-x-hidden overflow-y-auto p-0 sm:rounded-lg">
        <div
          aria-hidden
          className="bg-primary pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full opacity-[0.06] blur-3xl"
        />
        <div className="relative grid grid-cols-1 gap-6 p-6 sm:p-8">
          <DialogHeader>
            {/* TEMPLATE_TODO: set the eyebrow/kicker for this app (program · lab · workstream). */}
            <span className="text-primary text-xs font-medium tracking-[0.18em] uppercase">
              J&amp;J MedTech · Future of Work Lab
            </span>
            {/* TEMPLATE_TODO: replace headline + description with this app's one-line story. */}
            <DialogTitle className="font-heading text-foreground mt-2 text-2xl leading-tight font-normal tracking-tight sm:text-3xl">
              Welcome to Vision Deliver Intelligence
            </DialogTitle>
            <DialogDescription className="text-muted text-sm leading-relaxed">
              This is the baseline demo application for the Future of Work Lab: J&amp;J branding,
              sign-in, an admin portal, and the shared demo toolkit come pre-wired so the team can
              focus on the use case. Everything here runs on fictive data.
            </DialogDescription>
          </DialogHeader>

          <div className="border-border bg-background flex flex-col gap-3 rounded-md border p-5">
            <div className="flex items-center gap-2">
              <Sparkles className="text-primary h-4 w-4" />
              <p className="text-foreground text-sm font-semibold">What&rsquo;s live today</p>
            </div>
            {/* TEMPLATE_TODO: replace with the features that are actually live in this app. */}
            <ul className="text-foreground space-y-2 text-sm leading-6">
              <Bullet>J&amp;J-branded shell with sign-in and a guided tour of the app</Bullet>
              <Bullet>
                Feedback button on every page — category, rating, and a region screenshot
              </Bullet>
              <Bullet>
                Audience voting: attendees scan a QR, results reveal on the presenter&rsquo;s cue
              </Bullet>
              <Bullet>
                Admin portal with user management, activity stats, and feedback review
              </Bullet>
            </ul>
            <div className="mt-2 flex items-center gap-2">
              <CircleDot className="text-warm-gray-06 h-4 w-4" />
              <p className="text-muted text-sm font-semibold">Next on the roadmap</p>
            </div>
            {/* TEMPLATE_TODO: replace with this app's actual roadmap items. */}
            <ul className="text-muted-foreground space-y-2 text-sm leading-6">
              <Bullet tone="muted">The domain surfaces this demo is being built to show</Bullet>
              <Bullet tone="muted">
                Integration with live data inside the J&amp;J environment
              </Bullet>
              <Bullet tone="muted">Governed rollout beyond fictive data</Bullet>
            </ul>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* TEMPLATE_TODO: point the CTAs at this app's primary surfaces. */}
            <Button asChild size="lg" onClick={() => onOpenChange(false)}>
              <Link to="/home">Get started</Link>
            </Button>
            <Button asChild size="lg" variant="outline" onClick={() => onOpenChange(false)}>
              <Link to="/guide">
                Open the guide
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          <p className="text-muted-foreground text-xs">
            Fictive data for the sake of demonstrating functionality.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Bullet({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "muted";
}) {
  // Live items get a green check (status accent, used sparingly); roadmap
  // items a muted "not yet" dot.
  const Icon = tone === "muted" ? CircleDot : CheckCircle2;
  return (
    <li className="flex items-start gap-2">
      <Icon
        className={cn(
          "mt-0.5 h-4 w-4 shrink-0",
          tone === "muted" ? "text-warm-gray-06" : "text-jj-green-03",
        )}
      />
      <span>{children}</span>
    </li>
  );
}
